import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import NewPasswordForm from "../components/NewPasswordForm";
import ForgotPasswordForm from "../components/ForgotPasswordForm";
import "../styles/login.css";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, authError, clearAuthError, user, loading: authLoading } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false);
  const [showForgotPasswordForm, setShowForgotPasswordForm] = useState(false);

  // Obtener la ubicación desde donde vino el usuario (para redirigir después del login)
  const from = location.state?.from?.pathname || "/report";

  // El error que se muestra puede venir del estado local o del contexto (OAuth errors)
  const displayError = error || authError;

  // Si el usuario ya está autenticado, redirigir automáticamente
  useEffect(() => {
    if (!authLoading && user) {
      navigate(from, { replace: true });
    }
  }, [user, authLoading, navigate, from]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    clearAuthError(); // Limpiar errores de OAuth previos
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.success) {
        // Login exitoso - redirigir a la página original
        navigate(from, { replace: true });
      } else if (result.requiresPasswordChange) {
        // Mostrar formulario de cambio de contraseña
        setShowNewPasswordForm(true);
      } else if (result.requiresMFA) {
        setError("Se requiere código MFA. Esta funcionalidad aún no está implementada.");
      } else {
        setError(result.message || "Error al iniciar sesión");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message || "Error al iniciar sesión. Verifica tus credenciales.");
    } finally {
      setLoading(false);
    }
  };

  const handleNewPasswordSuccess = () => {
    // Después de cambiar contraseña exitosamente, redirigir
    navigate(from, { replace: true });
  };

  const handleForgotPasswordClick = (e) => {
    e.preventDefault();
    setShowForgotPasswordForm(true);
    setError("");
    clearAuthError();
  };

  const handleForgotPasswordCancel = () => {
    setShowForgotPasswordForm(false);
  };

  const handleForgotPasswordSuccess = () => {
    setShowForgotPasswordForm(false);
    setError("");
    alert("Contraseña reseteada exitosamente. Ahora puedes iniciar sesión con tu nueva contraseña.");
  };

  // Mostrar loading mientras se verifica la autenticación
  if (authLoading) {
    return (
      <div className="login-container">
        <div className="login-right" style={{ width: '100%' }}>
          <div className="login-card" style={{ textAlign: 'center' }}>
            <div className="spinner"></div>
            <p>Cargando...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      {/* LEFT SIDE */}
      <div className="login-left">
        <h2 className="system-title">
          SISTEMA <br /> VERIFICACION EPP
        </h2>
        <img
          src="/agrosuper.png"
          alt="Agrosuper"
          className="brand-logo"
        />
      </div>

      {/* RIGHT SIDE */}
      <div className="login-right">
        <div className="login-card">
          <h3 className="login-title">Login</h3>

          {/* Mostrar formulario de login, nueva contraseña o recuperación de contraseña */}
          {showNewPasswordForm ? (
            /* Formulario de cambio de contraseña */
            <NewPasswordForm onSuccess={handleNewPasswordSuccess} />
          ) : showForgotPasswordForm ? (
            /* Formulario de recuperación de contraseña */
            <ForgotPasswordForm
              onCancel={handleForgotPasswordCancel}
              onSuccess={handleForgotPasswordSuccess}
            />
          ) : (
            <>
              {/* Mensaje de error */}
              {displayError && (
                <div className="alert alert-error">
                  {displayError}
                </div>
              )}

              {/* Formulario de login */}
              <form onSubmit={handleLogin}>
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  autoComplete="email"
                  autoFocus
                />

                <input
                  type="password"
                  placeholder="Contraseña"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  autoComplete="current-password"
                />

                <div className="forgot">
                  <span onClick={handleForgotPasswordClick}>
                    ¿Olvidaste tu contraseña?
                  </span>
                </div>

                <button type="submit" disabled={loading}>
                  {loading ? "Ingresando..." : "Ingresar"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
